// ============================================================================
// CALCULATION ENGINES - Sustainability Assessment Platform
// Created by Salvador Janeiro
// ============================================================================

import {
  EMISSION_FACTORS,
  WATER_FACTORS,
  PLASTIC_FACTORS,
  BENCHMARKS,
  COMPANY_QUESTIONS
} from './data-constants';

// ============================================================================
// INDIVIDUAL CALCULATOR
// ============================================================================

export const calculateIndividualResults = (answers) => {
  let carbonTotal = 0;
  let waterTotal = 0;
  let plasticTotal = 0;
  
  const breakdown = {
    transportation: 0,
    homeEnergy: 0,
    diet: 0,
    fashion: 0,
    waste: 0,
    consumption: 0,
  };

  // --------------------------------------------------------------------------
  // TRANSPORTATION CALCULATIONS
  // --------------------------------------------------------------------------
  
  const commute = answers.commute || 'walking';
  const commuteDistance = parseFloat(answers.commuteDistance || 0);
  const annualCommuteDays = 250; // workdays per year
  
  if (commute !== 'walking' && commuteDistance > 0) {
    const dailyEmissions = EMISSION_FACTORS.transport[commute] * commuteDistance;
    breakdown.transportation += dailyEmissions * annualCommuteDays;
  }

  // Flights
  const flights = answers.flights || 'never';
  if (flights === 'short1-2') {
    breakdown.transportation += 500 * EMISSION_FACTORS.flights.short * 2; // 2 flights of 500km
  } else if (flights === 'several') {
    breakdown.transportation += (1000 * EMISSION_FACTORS.flights.short * 2) + 
                                 (3000 * EMISSION_FACTORS.flights.medium * 2);
  } else if (flights === 'frequent') {
    breakdown.transportation += (1000 * EMISSION_FACTORS.flights.short * 3) + 
                                 (3000 * EMISSION_FACTORS.flights.medium * 3) +
                                 (8000 * EMISSION_FACTORS.flights.long * 2);
  }

  // --------------------------------------------------------------------------
  // HOME ENERGY CALCULATIONS
  // --------------------------------------------------------------------------
  
  const energySource = answers.energySource || 'grid';
  const monthlyKwh = 300; // average household
  const householdSize = answers.householdSize === '1' ? 1 : 
                       answers.householdSize === '2-3' ? 2.5 : 4.5;
  
  breakdown.homeEnergy = (monthlyKwh * 12 * EMISSION_FACTORS.electricity[energySource]) / householdSize;

  // --------------------------------------------------------------------------
  // DIET CALCULATIONS
  // --------------------------------------------------------------------------
  
  const diet = answers.diet || 'mixedLowMeat';
  breakdown.diet = EMISSION_FACTORS.diet[diet] * 365;

  // Water footprint from diet
  waterTotal += WATER_FACTORS.dietAnnual[diet];

  // --------------------------------------------------------------------------
  // FASHION CALCULATIONS
  // --------------------------------------------------------------------------
  
  const clothingFreq = answers.clothingFrequency || 'rarely';
  const clothingImpactMap = {
    rarely: { items: 10, co2: 100, water: 27000 },
    sometimes: { items: 24, co2: 240, water: 64800 },
    frequently: { items: 60, co2: 600, water: 162000 },
  };
  
  breakdown.fashion = clothingImpactMap[clothingFreq].co2;
  waterTotal += clothingImpactMap[clothingFreq].water;

  // --------------------------------------------------------------------------
  // WASTE CALCULATIONS
  // --------------------------------------------------------------------------
  
  const recycling = answers.recycling || 'sometimes';
  const wasteMap = {
    always: { trash: 15, recycling: 25 },
    sometimes: { trash: 25, recycling: 15 },
    rarely: { trash: 40, recycling: 5 },
  };
  
  breakdown.waste = (wasteMap[recycling].trash * EMISSION_FACTORS.waste.trash * 12) +
                    (wasteMap[recycling].recycling * EMISSION_FACTORS.waste.recycling * 12);

  // Plastic waste
  const plasticUse = answers.singleUsePlastic || 'sometimes';
  const plasticMap = {
    hardlyEver: 5,
    sometimes: 28,
    often: 45,
  };
  plasticTotal = plasticMap[plasticUse];

  // --------------------------------------------------------------------------
  // WATER CONSERVATION
  // --------------------------------------------------------------------------
  
  const waterConsv = answers.waterConservation || 'moderate';
  const waterConsvMap = {
    extensive: 9125,   // 25L/day * 365
    moderate: 14600,   // 40L/day * 365
    minimal: 29200,    // 80L/day * 365
  };
  waterTotal += waterConsvMap[waterConsv];

  // --------------------------------------------------------------------------
  // ELECTRONICS & CONSUMPTION
  // --------------------------------------------------------------------------
  
  const electronics = answers.electronics || 'fewYears';
  const electronicsMap = {
    broken: 20,
    fewYears: 50,
    yearly: 150,
  };
  breakdown.consumption = electronicsMap[electronics];

  // --------------------------------------------------------------------------
  // CALCULATE TOTALS
  // --------------------------------------------------------------------------
  
  carbonTotal = Object.values(breakdown).reduce((sum, val) => sum + val, 0);

  // --------------------------------------------------------------------------
  // CALCULATE SUSTAINABILITY SCORE (0-100)
  // --------------------------------------------------------------------------
  
  let score = 100;
  
  // Carbon score (40% weight)
  const targetCarbon = BENCHMARKS.carbon.unTarget * 1000; // convert to kg
  const avgCarbon = BENCHMARKS.carbon.euAverage * 1000;
  const carbonScore = Math.max(0, Math.min(100, 
    ((avgCarbon - carbonTotal) / (avgCarbon - targetCarbon)) * 100
  ));
  score = carbonScore * 0.4;
  
  // Water score (25% weight)
  const avgWater = BENCHMARKS.water.globalAverage;
  const targetWater = BENCHMARKS.water.targetEfficient;
  const waterScore = Math.max(0, Math.min(100, 
    ((avgWater - waterTotal) / (avgWater - targetWater)) * 100
  ));
  score += waterScore * 0.25;
  
  // Plastic score (20% weight)
  const avgPlastic = BENCHMARKS.plastic.globalAverage;
  const plasticScore = Math.max(0, Math.min(100, 
    ((avgPlastic - plasticTotal) / avgPlastic) * 100
  ));
  score += plasticScore * 0.20;
  
  // Lifestyle habits score (15% weight)
  let habitScore = 0;
  if (answers.recycling === 'always') habitScore += 20;
  if (answers.ecoProducts === 'usually') habitScore += 20;
  if (answers.localFood === 'usually') habitScore += 20;
  if (answers.foodWaste === 'veryLittle') habitScore += 20;
  if (answers.clothingDisposal !== 'trash') habitScore += 20;
  score += habitScore * 0.15;

  // --------------------------------------------------------------------------
  // RETURN RESULTS
  // --------------------------------------------------------------------------
  
  return {
    carbonTotal: (carbonTotal / 1000).toFixed(2), // convert to tons
    waterTotal: Math.round(waterTotal),
    plasticTotal: Math.round(plasticTotal),
    breakdown,
    score: Math.round(score),
    answers,
  };
};

// ============================================================================
// COMPANY CALCULATOR
// ============================================================================

export const calculateCompanyResults = (answers) => {
  const categories = {
    environmental: 0,
    social: 0,
    governance: 0,
  };

  let maxScores = {
    environmental: 0,
    social: 0,
    governance: 0,
  };

  // --------------------------------------------------------------------------
  // SCORE EACH QUESTION
  // --------------------------------------------------------------------------
  
  COMPANY_QUESTIONS.forEach(question => {
    const answer = answers[question.id];
    if (!answer) return;

    const category = question.category.toLowerCase();
    
    if (question.type === 'multiple') {
      // Multiple choice questions (like SDGs)
      const selectedCount = Array.isArray(answer) ? 
        answer.filter(a => a !== 'none').length : 0;
      
      if (selectedCount === 0) {
        categories[category] += 0;
      } else if (selectedCount <= 2) {
        categories[category] += 5;
      } else if (selectedCount <= 4) {
        categories[category] += 10;
      } else {
        categories[category] += 15;
      }
      maxScores[category] += 15;
    } else {
      // Single choice questions
      const points = question.points[answer] || 0;
      categories[category] += points;
      maxScores[category] += Math.max(...Object.values(question.points));
    }
  });

  // --------------------------------------------------------------------------
  // CALCULATE PERCENTAGES
  // --------------------------------------------------------------------------
  
  const scores = {
    environmental: Math.round((categories.environmental / maxScores.environmental) * 100),
    social: Math.round((categories.social / maxScores.social) * 100),
    governance: Math.round((categories.governance / maxScores.governance) * 100),
  };

  const overallScore = Math.round(
    (scores.environmental + scores.social + scores.governance) / 3
  );

  // --------------------------------------------------------------------------
  // DETERMINE LEVEL
  // --------------------------------------------------------------------------
  
  let level, levelName, levelColor, levelDescription;
  
  if (overallScore < 30) {
    level = 1;
    levelName = 'Beginner';
    levelColor = '#E63946';
    levelDescription = 'Your company has just started on sustainability or has minimal measures in place.';
  } else if (overallScore < 60) {
    level = 2;
    levelName = 'Intermediate';
    levelColor = '#F77F00';
    levelDescription = 'Your company has some sustainability initiatives but with significant room to grow.';
  } else if (overallScore < 85) {
    level = 3;
    levelName = 'Advanced';
    levelColor = '#06D6A0';
    levelDescription = 'Your company actively manages sustainability in many areas with solid ESG performance.';
  } else {
    level = 4;
    levelName = 'Leader';
    levelColor = '#118AB2';
    levelDescription = 'Your company is a sustainability leader, meeting or exceeding most best practices.';
  }

  // --------------------------------------------------------------------------
  // RETURN RESULTS
  // --------------------------------------------------------------------------
  
  return {
    level,
    levelName,
    levelColor,
    levelDescription,
    overallScore,
    scores,
    categories,
    answers,
  };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getSustainabilityLevel = (score) => {
  if (score < 40) return { level: 'Low', color: '#E63946', badge: '🔴' };
  if (score < 70) return { level: 'Moderate', color: '#F77F00', badge: '🟡' };
  return { level: 'High', color: '#06D6A0', badge: '🟢' };
};

export const getCompanyLevelEmoji = (level) => {
  const emojiMap = {
    1: '🌱',
    2: '🌿',
    3: '🌳',
    4: '🏆'
  };
  return emojiMap[level] || '🌱';
};

export const calculateImpactEquivalents = (carbonTons) => {
  const carbon = parseFloat(carbonTons) * 1000; // convert to kg
  
  return {
    trees: (carbon / 21).toFixed(1), // Average tree absorbs ~21 kg CO2/year
    kmDriven: (carbon / 0.171).toFixed(0), // Average car emissions
    flights: (carbon / 400).toFixed(1), // Average short-haul flight
  };
};

export const calculateWaterEquivalents = (liters) => {
  const olympicPool = 2500000; // liters
  return {
    pools: (liters / olympicPool).toFixed(2),
    cubicMeters: (liters / 1000).toFixed(2),
  };
};
